import { components } from "@/api-schema";
import { sortByLabel } from "@/common/helpers/sort-by-label";
import { DeviceType } from "@/common/hooks/use-device";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { ImplementedAccessorColumnDef } from "@/components/data-table/types";
import { EmptyValue } from "@/components/ui/empty-value";
import { getBranchV1Identifier } from "@/modules/masterdata/branch/branch-identifiers";
import i18next from "i18next";
import { AddressLines } from "./_components/address-lines";
import { commonColumns } from "./_constants/common-columns";

export type BranchTableType = components["schemas"]["Branch"];

type BranchName = BranchTableType["name"];
type BranchCompany = BranchTableType["company"];
type BranchAddress = BranchTableType["address"];
type BranchBrands = BranchTableType["brands"];
type BranchV1Id = string | undefined;

export const branchTableDefaultColumns = {
  columnName: {
    id: "name",
    size: 100,
    enableSorting: true,
    header: () => i18next.t("common:Name"),
    accessorFn: (row: BranchTableType) => row.name,
    cell: (row) => {
      const name = row.getValue() as BranchName;
      return name ?? <EmptyValue />;
    },
  } as ImplementedAccessorColumnDef<BranchTableType, BranchName>,

  columnV1Id: {
    id: "v1Id",
    exportPaths: ["branchIdentifiers[type=V1].identifier"],
    size: 50,
    enableSorting: false,
    align: "right",
    header: () => i18next.t("masterdata:V1ID"),
    visibility: [DeviceType.Mobile, DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: BranchTableType) => getBranchV1Identifier(row),
    cell: (row) => {
      const v1Identifier = row.getValue();
      return v1Identifier ? automaticCellRenderer(v1Identifier) : <EmptyValue />;
    },
  } as ImplementedAccessorColumnDef<BranchTableType, BranchV1Id>,

  columnCompany: {
    id: "company",
    exportPaths: ["company.name"],
    size: 100,
    enableSorting: true,
    header: () => i18next.t("common:Company"),
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: BranchTableType) => row.company,
    cell: (row) => {
      const company = row.getValue() as BranchCompany;
      return company?.name ?? "";
    },
  } as ImplementedAccessorColumnDef<BranchTableType, BranchCompany>,

  columnAddress: {
    id: "address",
    exportPaths: ["address.street", "address.houseNumber", "address.postalCode", "address.city"],
    size: 200,
    enableSorting: true,
    header: () => i18next.t("masterdata:Address"),
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: BranchTableType) => row.address,
    cell: (row) => <AddressLines address={row.getValue() as BranchAddress | null} />,
  } as ImplementedAccessorColumnDef<BranchTableType, BranchAddress>,

  columnBrands: {
    id: "brands",
    exportPaths: ["brands.name"],
    size: 150,
    enableSorting: false,
    header: () => i18next.t("ram:Brands"),
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: BranchTableType) => row.brands,
    cell: (row) => {
      const brands = row.getValue() as BranchBrands;
      if (!brands || brands.length === 0) return <EmptyValue />;

      const sortedBrands = sortByLabel(brands, (brand) => brand.name ?? "");
      const displayBrands = sortedBrands.slice(0, 2);
      const remainingCount = brands.length - displayBrands.length;

      return (
        <div className="flex flex-col gap-1">
          {displayBrands.map((brand) => (
            <span key={brand.uuid} className="text-xs">
              {brand.name}
            </span>
          ))}
          {remainingCount > 0 && (
            <span className="text-muted-foreground text-xs">
              {i18next.t("common:MoreCount", { count: remainingCount })}
            </span>
          )}
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<BranchTableType, BranchBrands>,

  columnTechnicalId: {
    ...commonColumns.columnTechnicalId<BranchTableType>(),
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
  } as ImplementedAccessorColumnDef<BranchTableType, string>,
  columnUuid: commonColumns.columnUuid<BranchTableType>(),
  columnDeletedAt: commonColumns.columnDeletedAt<BranchTableType>(),
  columnCreatedAt: commonColumns.columnCreatedAt<BranchTableType>(),
  columnUpdatedAt: commonColumns.columnUpdatedAt<BranchTableType>(),
};
